/**
 * ComparisonTable — V03 Hybrid Grid
 *
 * Per design handoff: 5-col CSS grid, gray-50 header (orange-tinted Price column),
 * orange brand eyebrow + clean model name, em-dash for empty energy, rounded-full
 * orange pill CTA (with $X.XX → if priced, "See Price →" if not).
 *
 * Inline styles (not Tailwind classes) so the surface is unaffected by Next.js
 * dev-mode CSS chunk caching that has been intermittent in this workspace.
 *
 * ARIA: outer div has role=table, header & body rows are role=row with role=columnheader/cell
 * cells. The visual implementation uses CSS grid with display:contents on rows.
 */

import * as React from 'react'
import type { ArticleProductLookupState } from '@/lib/article-product-lookup'
import { PartLinkedText } from '@/components/filters/part-linked-text'
import type { PartLinkSegment } from '@/lib/filters/article-part-links'

const ORANGE = '#ff9951'
const GRAY_50 = '#f9fafb'
const GRAY_100 = '#f3f4f6'
const GRAY_200 = '#e5e7eb'
const GRAY_500 = '#6b7280'
const GRAY_700 = '#374151'
const GRAY_900 = '#111827'

const SYS_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

export interface ComparisonRow {
  brand: string
  modelName: string
  capacity: string
  dimensions: string
  /** Kept for back-compat (the article extractor still parses it from legacy
   *  HTML tables) but no longer rendered — energy moved into the inline
   *  product cards above each H2. */
  energy: string
  /** Kept for back-compat; no longer rendered. The "See Price" pill was
   *  removed in favour of pricing surfacing on the inline product cards
   *  earlier in the article body. */
  price?: number
  lookupState?: ArticleProductLookupState
  href: string
  /** Optional product image URL. The article extractor populates this for
   *  rows linking to known `/products/<slug>` pages. ~80px thumbnail. */
  imageUrl?: string
}

export interface ComparisonTableProps {
  rows: ComparisonRow[]
  columnLabels?: { capacity?: string; dimensions?: string }
}

export interface FeatureComparisonProps {
  headers: string[]
  rows: string[][]
  /**
   * FRIDGE-1087 — per cell, the filter numbers it names as linked segments
   * (lib/filters/article-part-links.ts `segmentFilterNumbers`), aligned with
   * `rows`; null or absent = the cell renders as its text.
   */
  cellLinks?: Array<Array<PartLinkSegment[] | null>>
}

/**
 * Feature comparison table — used by "X vs Y" articles where columns are products and rows are
 * feature labels. Same V03 visual chrome (gray-50 header, hairline rows, system sans, tabular nums)
 * but no brand eyebrow, no model name, and no See Price pill — just text in cells.
 *
 * Rows whose first-cell label matches `/^(energy|price|see\s*price|cost)/i`
 * are filtered out — same Phase A spec as the product round-up table: kWh
 * and price live in the inline product cards above each H2, not the
 * comparison table.
 */
export function FeatureComparisonTable({ headers, rows, cellLinks }: FeatureComparisonProps) {
  // Filter out energy + price rows — these live in the inline product cards
  // above each H2 in editorial articles, so the comparison table stays
  // focused on structural / capacity / dimension differences.
  const ENERGY_PRICE_LABEL = /^(energy|price|see\s*price|cost\s*to\s*run|annual\s*cost|kwh|electricity)/i
  // Filter by index so `cellLinks` stays aligned with the rows that remain.
  const visibleIdx = rows
    .map((row, i) => ({ row, i }))
    .filter(({ row }) => !ENERGY_PRICE_LABEL.test((row[0] || "").toString().trim()))
    .map(({ i }) => i)
  const visibleRows = visibleIdx.map((i) => rows[i])
  if (!visibleRows.length) return null
  const colCount = Math.max(headers.length, visibleRows[0]?.length || 0)
  const gridTemplate = colCount === 3 ? '1.2fr 1fr 1fr' : `1.2fr repeat(${colCount - 1}, 1fr)`

  const headerCellStyle = (i: number): React.CSSProperties => ({
    background: GRAY_50,
    padding: '13px 14px',
    fontFamily: SYS_FONT,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: GRAY_500,
    borderBottom: `1px solid ${GRAY_200}`,
    textAlign: i === 0 ? 'left' : 'center',
  })

  const bodyCellStyle = (rowIdx: number, colIdx: number): React.CSSProperties => ({
    padding: '14px 14px',
    fontFamily: SYS_FONT,
    fontSize: 13,
    color: colIdx === 0 ? GRAY_900 : GRAY_700,
    fontWeight: colIdx === 0 ? 700 : 400,
    borderTop: rowIdx === 0 ? 'none' : `1px solid ${GRAY_100}`,
    background: '#fff',
    textAlign: colIdx === 0 ? 'left' : 'center',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.45,
    display: 'flex',
    alignItems: 'center',
    justifyContent: colIdx === 0 ? 'flex-start' : 'center',
  })

  return (
    // Outer wrapper enables horizontal scroll on narrow viewports — feature
    // tables stay legible at desktop widths but scroll cleanly on mobile
    // instead of crushing columns.
    <div style={{ overflowX: 'auto', margin: '24px 0', WebkitOverflowScrolling: 'touch' }}>
    <div
      role="table"
      style={{
        display: 'grid',
        gridTemplateColumns: gridTemplate,
        border: `1px solid ${GRAY_200}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: '#fff',
        minWidth: 560,
        fontFamily: SYS_FONT,
      }}
    >
      <div role="row" style={{ display: 'contents' }}>
        {headers.map((h, i) => (
          <div role="columnheader" key={`h-${i}`} style={headerCellStyle(i)}>
            {h}
          </div>
        ))}
      </div>
      {visibleRows.map((row, ri) => (
        <div role="row" style={{ display: 'contents' }} key={`r-${ri}`}>
          {row.map((cell, ci) => (
            <div role="cell" key={`c-${ri}-${ci}`} style={bodyCellStyle(ri, ci)}>
              {/* A linked cell is ONE flex child (the cell is display:flex), so its
                  text, links and FFN chips wrap as a line instead of as columns. */}
              {cell ? (
                cellLinks?.[visibleIdx[ri]]?.[ci] ? (
                  <span>
                    <PartLinkedText text={cell} segments={cellLinks[visibleIdx[ri]][ci]} />
                  </span>
                ) : (
                  cell
                )
              ) : (
                '—'
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
    </div>
  )
}

/**
 * Quick Comparison Table — V03 product round-up.
 *
 * Three columns: Image | Model | Capacity | Dimensions. Energy + Price columns
 * were removed — kWh and price now live in the inline product cards above each
 * H2, which read better than a sparse 5-column table. The image column is
 * intentionally narrow (88px thumb) so the row doesn't stretch out vertically.
 *
 * The whole row is a click target wrapping `row.href` (the canonical product
 * URL), so dropping the explicit "See Price" pill doesn't lose the affiliate
 * conversion path — it just consolidates it into one tap.
 */
export function ComparisonTable({ rows, columnLabels = {} }: ComparisonTableProps) {
  const headerLabel = (): React.CSSProperties => ({
    background: GRAY_50,
    padding: '13px 14px',
    fontFamily: SYS_FONT,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: GRAY_500,
    borderBottom: `1px solid ${GRAY_200}`,
  })

  const bodyCell = (
    isFirstRow: boolean,
    isCenter = false,
  ): React.CSSProperties => ({
    padding: '14px',
    fontFamily: SYS_FONT,
    fontSize: 12.5,
    color: GRAY_700,
    borderTop: isFirstRow ? 'none' : `1px solid ${GRAY_100}`,
    background: '#fff',
    textAlign: isCenter ? 'center' : 'left',
    fontVariantNumeric: 'tabular-nums',
    display: 'flex',
    alignItems: 'center',
    justifyContent: isCenter ? 'center' : 'flex-start',
    lineHeight: 1.4,
  })

  return (
    <div style={{ overflowX: 'auto', margin: '24px 0', WebkitOverflowScrolling: 'touch' }}>
    <div
      role="table"
      style={{
        display: 'grid',
        gridTemplateColumns: '88px 2fr 1fr 1.4fr',
        border: `1px solid ${GRAY_200}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: '#fff',
        minWidth: 560,
        fontFamily: SYS_FONT,
      }}
    >
      {/* Header row — uses display: contents so cells participate in the grid */}
      <div role="row" style={{ display: 'contents' }}>
        <div role="columnheader" style={{ ...headerLabel(), textAlign: 'center' }} aria-label="Image">
          {/* visually empty, screen-reader-only label */}
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            Image
          </span>
        </div>
        <div role="columnheader" style={{ ...headerLabel(), textAlign: 'left' }}>
          Model
        </div>
        <div role="columnheader" style={{ ...headerLabel(), textAlign: 'center' }}>
          {columnLabels.capacity || 'Capacity'}
        </div>
        <div role="columnheader" style={{ ...headerLabel(), textAlign: 'center' }}>
          {columnLabels.dimensions || 'Dimensions'}
        </div>
      </div>

      {/* Body rows — wrap each in a link so the whole row taps through to the
          product page. Per-cell `display:contents` keeps the grid tracking. */}
      {rows.map((row, i) => {
        const isFirst = i === 0
        const available = !row.lookupState || row.lookupState === 'available'
        const Row = available ? 'a' : 'div'
        return (
          <Row
            role="row"
            href={available ? row.href : undefined}
            key={i}
            aria-label={`${row.brand} ${row.modelName}${available ? " — see details" : ""}`}
            style={{ display: 'contents', textDecoration: 'none', color: 'inherit' }}
          >
            <div
              role="cell"
              style={{
                ...bodyCell(isFirst, true),
                padding: '10px',
                background: '#fff',
              }}
            >
              {row.imageUrl ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                  src={row.imageUrl}
                  alt={`${row.brand} ${row.modelName} | Fridge.com`}
                  width={68}
                  height={68}
                  style={{
                    width: 68,
                    height: 68,
                    objectFit: 'contain',
                    background: GRAY_50,
                    borderRadius: 6,
                    display: 'block',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 68,
                    height: 68,
                    borderRadius: 6,
                    background: GRAY_50,
                    border: `1px solid ${GRAY_100}`,
                  }}
                />
              )}
            </div>
            <div role="cell" style={{ ...bodyCell(isFirst), flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
              <span
                style={{
                  fontFamily: SYS_FONT,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: '#0469f0',
                }}
              >
                {row.brand}
              </span>
              <span
                style={{
                  fontFamily: SYS_FONT,
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: GRAY_900,
                }}
              >
                {row.modelName}
              </span>
              {!available && (
                <span style={{ fontSize: 12, color: GRAY_500 }}>
                  {row.lookupState === 'lookup-failed' ? 'Details temporarily unavailable' : 'No longer in the current catalog'}
                </span>
              )}
            </div>
            <div role="cell" style={bodyCell(isFirst, true)}>
              {row.capacity || '—'}
            </div>
            <div role="cell" style={bodyCell(isFirst, true)}>
              {row.dimensions || '—'}
            </div>
          </Row>
        )
      })}
    </div>
    </div>
  )
}
